import { formatInstant } from '../../lib/timezone';
import type { MyDayListRow } from '../../data/database.types';

/**
 * MY DAY LOCAL NOTIFICATION PLANNING — WD-3 (as corrected by the independent
 * review, B1).
 *
 * **PURE.** This module turns authoritative server rows into a DESIRED
 * notification plan and diffs that plan against what the device actually has
 * SCHEDULED. It touches no native API, no clock of its own (`nowMs` is an
 * argument), no storage and no network — which is what makes the canonical
 * lead rule, the snooze rule, the recurrence shape and duplicate prevention
 * provable in unit tests without pretending a test can prove Android
 * delivery.
 *
 * **THE CANONICAL INSTANT IS SERVER TRUTH AND NOTHING ELSE (B1).** For an
 * ordinary timed occurrence there is EXACTLY ONE possible notification
 * moment: `due_at − 30 minutes`, derived from the server-authored absolute
 * instant. It is NEVER derived from `Date.now() + delay`, foreground time,
 * reconciliation time or the device timezone — the review rejected a
 * now-relative "prompt heads-up" precisely because a desired time that moves
 * with the clock re-arms on every reconciliation and can ring without bound.
 *
 * What happens around that one canonical instant:
 *
 *   * LEAD MOMENT STILL AHEAD  → the canonical alert is desired, and created
 *                                if absent.
 *   * LEAD MOMENT PASSED, ITEM STILL FUTURE → the canonical alert is desired
 *                                ONLY IF THE DEVICE ALREADY HOLDS IT — a
 *                                delayed inexact delivery is left alone — but
 *                                it is NEVER created after the fact. No
 *                                catch-up alert exists: the person just
 *                                created, rescheduled or foregrounded after
 *                                the lead moment, and Crewlink does not
 *                                manufacture a second meaning. Likewise a
 *                                DELIVERED or SWIPED alert is simply no
 *                                longer scheduled, and is never recreated.
 *   * DUE OR PAST              → nothing is desired at all; any lingering
 *                                Crewlink alert for the occurrence is
 *                                cancelled.
 *
 * THE REST OF THE LOCKED SEMANTICS (PT §14, D-051):
 *
 *   * DAY-ONLY   → no notification, ever. A whole-day promise has no o'clock
 *                  and Crewlink never invents one.
 *   * SNOOZED    → the notification moment is `snoozed_until` itself — the
 *                  wake-up point he chose. No 30-minute subtraction in either
 *                  direction, and no now-relative synthesis here either: a
 *                  lapsed snooze desires nothing new (the next authoritative
 *                  read re-buckets the row).
 *   * RECURRENCE → one row per series (WD-1's single active occurrence), so
 *                  at most ONE notification per item BY CONSTRUCTION.
 *   * PRIORITY   → changes nothing.
 */

/** The lead time, LOCKED at 30 minutes (D-051). Not user-configurable. */
export const NOTIFICATION_LEAD_MS = 30 * 60 * 1000;

/** ONE desired local notification. `itemId` is LOCAL MAPPING METADATA —
 * it identifies which follow-up a pending notification belongs to during
 * reconciliation and never appears in visible content. */
export interface PlannedMyDayNotification {
  readonly itemId: string;
  /** The CANONICAL absolute instant, ISO. Derived from server truth alone. */
  readonly at: string;
  /**
   * Whether a MISSING alert may be created (B1). True only while the
   * canonical instant is still ahead. False means: keep a matching alert the
   * device already holds (delayed inexact delivery), but NEVER create one —
   * no catch-up, no re-fire after delivery or a swipe.
   */
  readonly createIfAbsent: boolean;
  /** Visible content — bounded, no ids, no handles, no countdown text. */
  readonly title: string;
  readonly body: string;
}

/** What the device reports as pending, as far as planning cares. */
export interface PendingMyDayNotification {
  /** The OS-level numeric id — a local implementation detail. */
  readonly id: number;
  /** Which follow-up this belongs to, from the notification's own metadata.
   * Null for notifications Crewlink did not schedule — those are UNTOUCHED. */
  readonly itemId: string | null;
  /** The instant it was scheduled for, from the same metadata. */
  readonly at: string | null;
  /**
   * Which signed-in Crewlink user this alert was scheduled FOR (M2) — local
   * non-visible metadata, never authority. Null on a legacy Crewlink alert
   * scheduled before ownership existed, which authenticated reconciliation
   * treats as stale and removes.
   */
  readonly ownerId: string | null;
}

/** The visible notification title — the product speaking, not a record. */
export const NOTIFICATION_TITLE = 'My Day follow-up';

/**
 * The visible body: the follow-up's own words plus ABSOLUTE schedule context
 * in the CANONICAL Crewlink zone. Never "in 30 minutes" — the OS may deliver
 * late, and a countdown that has drifted is worse than no countdown.
 */
function bodyOf(row: MyDayListRow, kind: 'lead' | 'snooze'): string {
  if (kind === 'snooze') {
    return row.due_time === null || row.due_at === null
      ? `${row.title} — snoozed until now`
      : `${row.title} — due ${formatInstant(row.due_at, row.crewlink_timezone, {
          dateStyle: 'medium',
          timeStyle: 'short',
        })}`;
  }
  return `${row.title} — due ${formatInstant(row.due_at ?? '', row.crewlink_timezone, {
    dateStyle: 'medium',
    timeStyle: 'short',
  })}`;
}

/**
 * The DESIRED plan for the caller's open follow-ups, from server truth alone.
 *
 * At most one entry per row, therefore at most one per item — the reader
 * returns one row per follow-up, recurring series included (single active
 * occurrence, D-049).
 */
export function planMyDayNotifications(
  rows: readonly MyDayListRow[],
  nowMs: number,
): readonly PlannedMyDayNotification[] {
  const plan: PlannedMyDayNotification[] = [];

  for (const row of rows) {
    // ---- SNOOZED: the snooze moment is the wake-up point. -----------------
    // The SERVER said this row is resting (the bucket is its own answer); the
    // wake-up fires at the instant he chose, day-only and timed alike, with
    // no 30-minute subtraction — he picked the moment himself. A wake-up
    // whose moment has passed is DESIRED-IF-HELD only: a delayed inexact
    // delivery stays armed, but nothing new is manufactured for a lapse the
    // next authoritative read will re-bucket anyway.
    if (row.bucket === 'snoozed') {
      if (row.snoozed_until === null) continue;
      const wakeMs = Date.parse(row.snoozed_until);
      if (!Number.isFinite(wakeMs)) continue;
      plan.push({
        itemId: row.id,
        at: new Date(wakeMs).toISOString(),
        createIfAbsent: wakeMs > nowMs,
        title: NOTIFICATION_TITLE,
        body: bodyOf(row, 'snooze'),
      });
      continue;
    }

    // ---- DAY-ONLY: no time, no alert. -------------------------------------
    if (row.due_time === null || row.due_at === null) continue;

    // ---- TIMED: the ONE canonical instant, `due_at − 30m` (B1). -----------
    const dueMs = Date.parse(row.due_at);
    if (!Number.isFinite(dueMs)) continue;
    // Due or past: nothing is desired at all — the diff cancels any lingering
    // Crewlink alert for this occurrence, and the screen says Overdue.
    if (dueMs <= nowMs) continue;

    const leadMs = dueMs - NOTIFICATION_LEAD_MS;
    // **NEVER A CLOCK-RELATIVE SUBSTITUTE.** When the lead moment has passed
    // the canonical alert is kept IF the device still holds it (delayed
    // inexact delivery) and is otherwise simply absent — created-after-the-
    // fact catch-up alerts were the review's B1 blocker: a desired time that
    // moves with the clock re-arms on every reconciliation.
    plan.push({
      itemId: row.id,
      at: new Date(leadMs).toISOString(),
      createIfAbsent: leadMs > nowMs,
      title: NOTIFICATION_TITLE,
      body: bodyOf(row, 'lead'),
    });
  }

  return plan;
}

/* ==========================================================================
 * Reconciliation — desired plan vs device pending, as a pure diff
 * ========================================================================== */

export interface MyDayNotificationSchedule {
  readonly id: number;
  readonly itemId: string;
  /** The signed-in user this alert is FOR (M2) — metadata, never authority. */
  readonly ownerId: string;
  readonly at: string;
  readonly title: string;
  readonly body: string;
}

export interface MyDayNotificationDiff {
  /** OS ids to cancel — ours and obsolete, or ours and being replaced. */
  readonly cancel: readonly number[];
  /** Notifications to schedule, each with a stable collision-free OS id. */
  readonly schedule: readonly MyDayNotificationSchedule[];
}

/**
 * A DETERMINISTIC 32-bit id from the item UUID (FNV-1a), for the native APIs
 * that demand an int. **The number is an OS key, never the identity** —
 * matching between pending and desired happens on `itemId`, carried in the
 * notification's own metadata, so this id does not even need to be stable
 * across sessions for correctness. Collisions are handled, not hoped away:
 * `stableNotificationId` probes forward past any id already in use.
 */
export function hashNotificationId(itemId: string): number {
  let hash = 0x811c9dc5;
  for (let index = 0; index < itemId.length; index += 1) {
    hash ^= itemId.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  // Positive space well inside a Java int: Android ids are 32-bit signed,
  // so the base lands in [1, 2^30] and the probe below can walk upward
  // without ever approaching the sign bit.
  return (hash >>> 2) + 1;
}

export function stableNotificationId(itemId: string, used: ReadonlySet<number>): number {
  let id = hashNotificationId(itemId);
  // Linear probe on collision — deterministic given the same used set, and
  // bounded: the used set is at most the pending-notification count.
  while (used.has(id)) {
    id = id >= 0x7ffffffe ? 1 : id + 1;
  }
  return id;
}

/**
 * The diff: what to cancel, what to schedule, nothing else.
 *
 * **IDEMPOTENT BY CONSTRUCTION**: a scheduled notification whose item and
 * canonical instant match the desired plan is left exactly as it is, so
 * running reconciliation any number of times produces empty work after the
 * first. **A DESIRED ENTRY WITH `createIfAbsent: false` NEVER SCHEDULES**
 * (B1): it exists only to KEEP a matching alert the device already holds —
 * a delayed inexact delivery — and its absence (delivered, swiped, or never
 * scheduled) stays absence. **UNRELATED NOTIFICATIONS ARE UNTOUCHED**:
 * anything without Crewlink's own metadata is not ours to cancel.
 *
 * **OWNERSHIP (M2):** `currentUserId` is the signed-in Crewlink user this
 * reconciliation runs FOR. A Crewlink alert scheduled for a DIFFERENT user —
 * or a legacy Crewlink alert with no owner marker at all — is stale on this
 * session and is cancelled, so one person's follow-up title can never
 * surface on another person's lock screen. The owner id is local matching
 * metadata only; it grants nothing and never leaves the device.
 */
export function diffMyDayNotifications(
  desired: readonly PlannedMyDayNotification[],
  pending: readonly PendingMyDayNotification[],
  currentUserId: string,
): MyDayNotificationDiff {
  const desiredByItem = new Map(desired.map((entry) => [entry.itemId, entry]));

  const cancel: number[] = [];
  /** Ours, still correct, kept — the no-op set that makes reruns idempotent. */
  const satisfied = new Set<string>();
  const usedIds = new Set<number>();

  for (const entry of pending) {
    if (entry.itemId === null) {
      // Not Crewlink's. Its id still occupies the OS namespace, so record it
      // to keep new ids clear of it — but never cancel it.
      usedIds.add(entry.id);
      continue;
    }
    // **ANOTHER USER'S ALERT, OR A LEGACY ONE WITH NO OWNER, IS STALE HERE.**
    if (entry.ownerId !== currentUserId) {
      cancel.push(entry.id);
      continue;
    }
    const want = desiredByItem.get(entry.itemId);
    if (want !== undefined && entry.at === want.at && !satisfied.has(entry.itemId)) {
      satisfied.add(entry.itemId);
      usedIds.add(entry.id);
      continue;
    }
    // Obsolete (item no longer eligible), changed (replace), or a duplicate
    // of an already-satisfied item: cancelled. Its id becomes reusable.
    cancel.push(entry.id);
  }

  const schedule: MyDayNotificationSchedule[] = [];
  for (const want of desired) {
    if (satisfied.has(want.itemId)) continue;
    // **B1: A MISSING ALERT PAST ITS CANONICAL MOMENT STAYS MISSING.**
    if (!want.createIfAbsent) continue;
    const id = stableNotificationId(want.itemId, usedIds);
    usedIds.add(id);
    schedule.push({
      id,
      itemId: want.itemId,
      ownerId: currentUserId,
      at: want.at,
      title: want.title,
      body: want.body,
    });
  }

  return { cancel, schedule };
}
